import type { LeadAttributionInputObject } from "@app/queries/onboardingPage_LeadStartMutation.graphql";

// Where a visitor came from on the way into the funnel.
//
// Every "Get started" link on cxcash.com says which page and button it sits
// on, where the visit to the site began and what sent the visitor there. Only
// the address the link opens carries that, so the browser keeps it until a
// name opens a lead row, and leadStart writes it there for good.
//
// The first tagged arrival wins and is never overwritten. A second link, or a
// return from QuickBooks, Stripe, Google or Microsoft (which brings `oauth=`
// and no tags), must not rewrite how someone first got here.
const KEY = "cxcash.onboarding.attribution";

// The link's query parameter for each field leadStart takes. Nothing else in
// an address is kept.
const FIELD_BY_PARAM = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
  feature: "feature",
  page: "page",
  landing: "landing",
  ref: "ref",
} as const;

const FIELDS = [
  ...Object.values(FIELD_BY_PARAM),
  "referrer",
  "entryPath",
] as const;

// A control character in a tag is never part of a real link. The server cleans
// them out, but a stored copy that ever made leadStart fail would be resent
// from this browser on every try, since it is only forgotten once a row holds
// it. So none is kept, and none is sent.
// eslint-disable-next-line no-control-regex -- finding control characters is the point
const CONTROL = /[\u0000-\u001f\u007f]/;

// Run once, before the router: it rewrites some addresses on the way in
// (/sign-up redirects to /onboarding with the query dropped), and this is the
// only moment the address the visitor arrived at is still in the bar.
export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const tagged: LeadAttributionInputObject = {};
  for (const [param, field] of Object.entries(FIELD_BY_PARAM)) {
    const value = params.get(param);
    if (value && !CONTROL.test(value)) tagged[field] = value;
  }
  // An untagged arrival is a return trip or a typed address, not a source.
  if (Object.keys(tagged).length === 0 || storedAttribution()) return;

  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({
        ...tagged,
        referrer: document.referrer || undefined,
        entryPath: window.location.pathname,
      })
    );
  } catch {
    /* forgetting is the fallback */
  }
}

// Rebuilt field by field rather than passed through: the stored copy can
// predate this code, and the schema refuses the whole leadStart over one field
// it does not declare. Every access is guarded, like lead-token.ts.
export function storedAttribution(): LeadAttributionInputObject | null {
  try {
    const stored: unknown = JSON.parse(
      window.localStorage.getItem(KEY) ?? "null"
    );
    if (stored === null || typeof stored !== "object") return null;

    const kept: LeadAttributionInputObject = {};
    for (const field of FIELDS) {
      const value = (stored as Record<string, unknown>)[field];
      if (typeof value === "string" && value && !CONTROL.test(value))
        kept[field] = value;
    }
    return Object.keys(kept).length > 0 ? kept : null;
  } catch {
    return null;
  }
}

// Once the row holds it, the browser's copy has done its job, and a later run
// from this browser should start from the link it came through.
export function forgetAttribution() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* forgetting is the fallback */
  }
}
